import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { PIPEFITTER_CHART_COLORS } from "../constants/uiConstants";

export interface ProjectStatusChartData {
  series: Array<{ name: string; type?: string; data: number[] }>;
  options: { labels?: string[] };
  validPipes: number;
}

interface ProjectStatusChartProps {
  chartData: ProjectStatusChartData;
  targetPipeEnds?: number | null;
}

const TARGET_LINE_COLOR = "#1F9A3A";

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          padding: "10px 14px",
          fontSize: 11,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <p style={{ color: "black", marginBottom: 6, fontWeight: 600 }}>Date: {label}</p>
        {payload.map((p) => (
          <p key={p.name} style={{ color: p.color, margin: "2px 0" }}>
            {p.name}: {p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function ProjectStatusChart({ chartData, targetPipeEnds }: ProjectStatusChartProps) {
  const labels = chartData.options?.labels || [];
  const scanData = chartData.series[0]?.data || [];
  const cnsData = chartData.series[1]?.data || [];

  const data = labels.map((date, i) => ({
    date,
    scans: scanData[i] ?? 0,
    cns: cnsData[i] ?? 0,
  }));

  if (data.length === 0) {
    return null;
  }

  const maxScans = Math.max(...data.map((d) => d.scans), 1);
  const maxCns = Math.max(...data.map((d) => d.cns), 1);
  const hasTarget =
    targetPipeEnds != null && Number.isFinite(targetPipeEnds) && targetPipeEnds >= 0;
  const leftAxisMax = Math.max(maxScans + 10, hasTarget ? targetPipeEnds : 0, 1);

  const renderLeftAxisTick = ({
    x,
    y,
    payload,
  }: {
    x?: number;
    y?: number;
    payload?: { value: number };
  }) => {
    const value = payload?.value ?? 0;
    const isTarget = hasTarget && value === targetPipeEnds;
    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor="end"
        fill={isTarget ? TARGET_LINE_COLOR : "black"}
        fontSize={10}
        fontWeight={isTarget ? 700 : 400}
      >
        {value}
      </text>
    );
  };

  return (
    <div
      style={{
        background: "whitesmoke",
       // fontFamily: "'Georgia', serif",
        padding: "10px 20px",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 8, maxWidth: "100%" }}>
        <p
          style={{
            color: "green",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: 3,
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          Pipe scan progress
        </p>
        <h1 style={{ color: "black", fontSize: 20, fontWeight: 700, margin: "0 0 6px" }}>
          Project Status — Scans per Day & Cumulative
        </h1>
        <p style={{ color: "black", fontSize: 14, fontWeight: 500, margin: 0 }}>
          Daily pipe scans and <em>Cumulative Number of Scans (CNS)</em> over time
        </p>
      </div>

      {/* Total Valid Pipes badge */}
      {/* <div style={{ display: "flex", justifyContent: "center", marginBottom: 24, marginTop: 16 }}>
        <div
          style={{
            background: "white",
            border: "2px solid #60b885",
            borderRadius: 10,
            padding: "8px 24px",
            textAlign: "center",
          }}
        >
          <div style={{ color: "#60b885", fontWeight: 700, fontSize: 14 }}>Total Valid Pipes</div>
          <div style={{ color: "black", fontSize: 22, fontWeight: 800 }}>{data.length}</div>
        </div>
      </div> */}

      {/* Chart */}
      <div
        style={{
          background: "white",
          borderRadius: 16,
          padding: "30px 20px 20px",
          width: "100%",
          border: "1px solid #e5e7eb",
        }}
      >
        <ResponsiveContainer width="100%" height={312}>
          <ComposedChart data={data} margin={{ top: 10, right: 50, left: 10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: "black", fontSize: 10 }}
              label={{ value: "Date", position: "insideBottom", offset: -10, fill: "black", fontSize: 11 }}
            />
            <YAxis
              yAxisId="left"
              tick={renderLeftAxisTick}
              label={{
                value: "Number of Pipe Scan",
                angle: -90,
                position: "insideLeft",
                offset: 10,
                fill: "black",
                fontSize: 10,
              }}
              domain={[0, leftAxisMax]}
              allowDecimals={false}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: "black", fontSize: 10 }}
              label={{
                value: "CNS",
                angle: 90,
                position: "insideRight",
                offset: 10,
                fill: "black",
                fontSize: 10,
              }}
              domain={[0, maxCns]}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ paddingTop: 16 }} formatter={(value) => <span style={{ color: "black" }}>{value}</span>} />
            {hasTarget && (
              <ReferenceLine
                yAxisId="left"
                y={targetPipeEnds}
                stroke={TARGET_LINE_COLOR}
                strokeWidth={2}
                ifOverflow="extendDomain"
                label={{
                  value: `Target (${targetPipeEnds})`,
                  fill: TARGET_LINE_COLOR,
                  fontSize: 10,
                  position: "insideTopRight",
                }}
              />
            )}
            <Bar
              yAxisId="left"
              dataKey="scans"
              name="Number of Scan/Day"
              fill={PIPEFITTER_CHART_COLORS.primary}
              radius={[4, 4, 0, 0]}
              barSize={32}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="cns"
              name="CNS"
              stroke={PIPEFITTER_CHART_COLORS.secondaryOrange}
              strokeWidth={3}
              dot={{ fill: PIPEFITTER_CHART_COLORS.secondaryOrange, r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Key insight */}
      <div
        style={{
          marginTop: 24,
          background: "white",
          borderRadius: 12,
          padding: "16px 24px",
          border: "1px solid #e5e7eb",
          textAlign: "center",
        }}
      >
        <p style={{ color: "#4a5568", fontSize: 12, fontWeight: 500, margin: 0 }}>
          <strong style={{ color: "black" }}>How to read this:</strong> Bars show pipes scanned per day. The orange line shows
          cumulative scans (CNS) over time.
        </p>
      </div>
    </div>
  );
}
